import {
  Button,
  ButtonGroup,
  Center,
  HStack,
  Heading,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Stack,
  Text,
  useToast,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { getHeadersForRequest } from "../utils/functions";
import axios from "axios";
import { Loading } from "../components/Loading";
import { PaymentCard } from "../components/PaymentCard";
import Nav from "../components/navbar";
import { PaymentsList } from "../utils/types";
import { FaEye, FaPencilAlt, FaTrash } from "react-icons/fa";
import useCustomTranslation from "../hooks/useCustomTranslation";

const CustomerPaymentsScreen = () => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showConfirmationModal, setShowConfirmationModal] =
    useState<boolean>(false);
  const [selectedTransaction, setSelectedTransaction] = useState<number>(0);
  const [modalIsLoading, setModalIsLoading] = useState<boolean>(false);
  const [transactions, setTransactions] = useState<PaymentsList[]>([]);
  const { t, changeLanguage } = useCustomTranslation();
  const toast = useToast();
  const { customerId, customerName } = useParams<{
    customerId: string;
    customerName: string;
  }>();

  useEffect(() => {
    setIsLoading(true);
    getAllTransactions();
  }, []);

  const getAllTransactions = async () => {
    try {
      const today = new Date();
      today.setDate(today.getDate() - 90);
      const startDate = today;
      const endDate = new Date();

      const oldHeaders = await getHeadersForRequest();
      const newHeaders = {
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        id: customerId,
      };
      const headers = { ...oldHeaders, ...newHeaders };

      const response = await axios.get<PaymentsList[]>(
        `${process.env.REACT_APP_TEST_API_URL}/payments/customer`,
        {
          headers,
        }
      );

      const filteredOrdersPaymentPending = await response.data.filter(
        (transaction) => transaction.isVoid === false
      );

      setTransactions(
        filteredOrdersPaymentPending.sort(
          (a, b) =>
            new Date(b.paymentDate).getTime() -
            new Date(a.paymentDate).getTime()
        )
      );
      setIsLoading(false);
    } catch (error) {
      console.error(error);
      setIsLoading(false);
    }
  };

  const groupedTransactions: {
    date: string;
    transactions: PaymentsList[];
  }[] = [];

  groupedTransactions.sort((a, b) => {
    const dateA = new Date(a.date).getTime();
    const dateB = new Date(b.date).getTime();
    return dateB - dateA;
  });

  for (let transaction of transactions) {
    const date = new Date(transaction.paymentDate).toLocaleDateString();
    const existingGroup = groupedTransactions.find(
      (group) => group.date === date
    );
    if (existingGroup) {
      existingGroup.transactions.push(transaction);
    } else {
      groupedTransactions.push({ date, transactions: [transaction] });
    }
  }

  const handleTransactionClick = (transaction: number) => {
    console.log(transaction);
    setShowConfirmationModal(true);
    setSelectedTransaction(transaction);
  };

  const voidTransaction = async () => {
    setModalIsLoading(true);
    try {
      const headers = await getHeadersForRequest();
      await axios
        .post(
          `${process.env.REACT_APP_TEST_API_URL}/payments/delete`,
          {
            transactionId: selectedTransaction,
          },
          { headers }
        )
        .then((response) => {
          console.log(response.data);
          toast({
            title: t("Transaction VOID successful"),
            duration: 3000,
            status: "success",
          });
          setShowConfirmationModal(false);
          getAllTransactions();
          setModalIsLoading(false);
        })
        .catch((error) => {
          console.error(error);
          setModalIsLoading(false);
          setShowConfirmationModal(false);
          toast({
            title: t("Error, please try again later"),
            duration: 3000,
            status: "error",
          });
        });
    } catch (error) {
      toast({ title: "Error", status: "error" });
      console.error(error);
    }
  };

  return (
    <Stack>
      <Stack position="sticky" top={0} zIndex={1} width="full">
        <Nav pageName={"Payment History"}/>
      </Stack>
      <Stack flex={1}>
        <Stack pt={2} pb={10} px={3}>
          {isLoading ? (
            <Center h={600}>
              <Loading />
            </Center>
          ) : (
            <>
              <Text>{customerName}</Text>
              <Text>
                {t("Record count:")}
                {groupedTransactions.length}
              </Text>
              {groupedTransactions.map((groupedTransaction, index) => (
                <Stack key={index}>
                  <Heading size="md" my={2}></Heading>
                  {groupedTransaction.transactions.map((transaction, index) => (
                    <HStack>
                      <Stack
                        flex={1}
                        px={5}
                        key={index}
                        onContextMenu={(e) => {
                          e.preventDefault();
                          if (transaction.isVoid) {
                            toast({
                              title: t("Cannot edit a VOID receipt"),
                            });
                          } else {
                            handleTransactionClick(transaction.id);
                          }
                        }}
                      >
                        <PaymentCard payment={transaction} />
                      </Stack>

                      <Stack>
                        <IconButton
                          size="lg"
                          fontSize="lg"
                          // variant="ghost"
                          // color="current"
                          marginLeft="2"
                          onClick={() => {
                            if (transaction.isVoid) {
                              toast({
                                description: t("Cannot edit a VOID receipt"),
                                status: "warning",
                                duration: 9000,
                                isClosable: true,
                              });
                            } else {
                              handleTransactionClick(transaction.id);
                            }
                          }}
                          icon={<FaTrash />}
                          aria-label={`Delete Payment`}
                        />

                        {/* <IconButton
                          size="lg"
                          fontSize="lg"
                          // variant="ghost"
                          // color="current"
                          marginLeft="2"
                          onClick={() => {
                            // navigateToOrderDetails(transaction.transactionNumber)
                          }}
                          icon={<FaEye />}
                          aria-label={`Edit Transaction Details`}
                        /> */}
                      </Stack>
                    </HStack>
                  ))}
                </Stack>
              ))}
              {groupedTransactions.length < 1 && (
                <Stack alignItems={"center"} justifyContent={"center"} h={400}>
                  <Text fontSize={20} textAlign={"center"}>
                    {t("There are no Transactions to show")}
                  </Text>
                  <Text fontSize={20} textAlign={"center"}>
                    {t(
                      "Go to the Sales screen to record your first transaction!"
                    )}
                  </Text>
                </Stack>
              )}
            </>
          )}

          {/* CONFIRMATION MODAL */}
          <Modal
            isCentered
            closeOnOverlayClick={false}
            isOpen={showConfirmationModal}
            onClose={() => setShowConfirmationModal(false)}
          >
            <ModalContent maxWidth="350">
              <ModalCloseButton />
              <ModalHeader>{t("Alert")}</ModalHeader>
              <ModalBody>
                <Stack>
                  <Text textAlign={"center"}>
                    {t("Are you sure you want to VOID this receipt?")}
                  </Text>
                  <Text textAlign={"center"}>
                    {t("THIS ACTION IS IRREVERSIBLE!")}
                  </Text>
                </Stack>
              </ModalBody>
              <ModalFooter>
                <ButtonGroup>
                  <Button
                    variant="ghost"
                    colorScheme="blueGray"
                    onClick={() => {
                      setShowConfirmationModal(false);
                    }}
                  >
                    {t("No")}
                  </Button>
                  <Button
                    bg={"red"}
                    isLoading={modalIsLoading}
                    onClick={() => {
                      voidTransaction();
                    }}
                  >
                    {t("Yes")}
                  </Button>
                </ButtonGroup>
              </ModalFooter>
            </ModalContent>
          </Modal>
        </Stack>
      </Stack>
    </Stack>
  );
};

export default CustomerPaymentsScreen;
